import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, of, startWith, Subscription, switchMap, take } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { getSelectorByIdentifier } from '../../../store/selectors';
import { clearAutocomplete } from '../../../store/actions';
import { AutocompleteField } from '../../../store/models/field-models';
import { extractFieldErrorMessages } from '../utils';
import { ValidationRuleType } from '../../../store/models/validator-models';
import { SelectableOption, SelectableValue } from '../../../store/models/selectables';

@Component({
    selector: 'app-autocomplete-field',
    templateUrl: './autocomplete-field.component.html',
    styleUrls: ['./autocomplete-field.component.scss'],
})
export class AutocompleteFieldComponent implements OnInit, OnChanges, OnDestroy {
    @Input() field!: AutocompleteField<SelectableOption, SelectableValue>;
    @Input() control!: FormControl;
    @Input() fieldId!: string;

    options$!: Observable<SelectableOption[]>;
    filteredOptions$!: Observable<SelectableOption[]>;

    // Only for autocomplete with autoClearOnFocus
    displayControl: FormControl = new FormControl('');
    private subscriptions = new Subscription();

    constructor(private store: Store, private translate: TranslateService) {}

    ngOnInit(): void {
        this.initializeOptions();
        this.updateDisabledState();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Check if field or control has changed
        if ((changes.control && !changes.control.firstChange) || (changes.field && !changes.field.firstChange)) {
            this.cleanupSubscriptions();
            this.initializeOptions();
            this.updateDisabledState();
        }
    }

    ngOnDestroy(): void {
        this.cleanupSubscriptions();
    }

    private cleanupSubscriptions(): void {
        if (this.subscriptions) {
            this.subscriptions.unsubscribe();
            this.subscriptions = new Subscription();
        }
    }

    private updateDisabledState(): void {
        if (this.field?.disabled || this.control.disabled) {
            this.control.disable({ emitEvent: false });
            this.displayControl.disable({ emitEvent: false });
        } else {
            this.displayControl.enable({ emitEvent: false });
        }
    }

    private initializeOptions(): void {
        if (this.field?.optionsSelector) {
            // Cast the unknown type to TOption[] since we know the shape matches
            this.options$ = this.store.select(getSelectorByIdentifier(this.field.optionsSelector));

            // Initialize display value if using separate control
            if (this.field?.autoClearOnFocus && this.control.value) {
                this.syncDisplayControlWithModelValue();
            }

            this.setupAutocompleteFiltering();
        }
    }

    private setupAutocompleteFiltering(): void {
        // Set up filtering based on which control to use
        if (this.field?.autoClearOnFocus) {
            // Filter options based on display control
            this.filteredOptions$ = this.displayControl.valueChanges.pipe(
                startWith(''),
                switchMap((inputValue) => this.filterAutocompleteOptions(inputValue || '')),
            );
        } else {
            // Standard filtering on the main control
            this.filteredOptions$ = this.control.valueChanges.pipe(
                startWith(this.control.value),
                switchMap((inputValue) => this.filterAutocompleteOptions(inputValue || '')),
            );
        }
    }

    get isVisible(): boolean {
        return this.field?.visible !== false;
    }

    get isRequired(): boolean {
        return this.field?.validators?.some((v) => v.type === ValidationRuleType.Required) || false;
    }

    get hasError(): boolean {
        return this.control?.invalid && (this.control?.touched || this.control?.dirty);
    }

    get isDisabled(): boolean {
        return this.field?.disabled === true;
    }

    getPlaceholder(): string {
        if (!this.field.placeholder) {
            return '';
        }

        if (this.field.placeholder && this.field.placeholderSuffix) {
            return `${this.translate.instant(this.field.placeholder)} (${this.field.placeholderSuffix})`;
        }

        return this.translate.instant(this.field.placeholder);
    }

    /**
     * Gets the option value using valueMapper if provided, otherwise falls back to default properties
     */
    getValue(option: SelectableOption): SelectableValue {
        return this.field.valueMapper(option);
    }

    /**
     * Gets the display text using displayMapper if provided, otherwise falls back to default properties
     */
    getDisplayText(option: SelectableOption): string {
        return this.field.displayMapper(option);
    }

    get errorMessages(): string[] {
        return extractFieldErrorMessages(this.control, this.field, this.translate);
    }

    syncDisplayControlWithModelValue(): void {
        this.displayControl.setValue(this.control.value);
    }

    onAutocompleteOpened(): void {
        // Only clear input and show all options if autoClearOnFocus is enabled
        if (this.field?.autoClearOnFocus) {
            this.displayControl.setValue('', { emitEvent: true });
        }
    }

    onAutocompleteClosed(): void {
        // Only restore value if we're using the separate display control
        if (this.field?.autoClearOnFocus && !this.displayControl.value) {
            this.syncDisplayControlWithModelValue();
        }
    }

    onAutocompleteBlur(): void {
        // Only use the display control value if we're in autoClearOnFocus mode
        const valueToPass = this.field?.autoClearOnFocus ? this.displayControl.value : this.control.value;

        if (this.field.events?.onBlur) {
            this.field.events.onBlur(valueToPass).forEach((action) => this.store.dispatch(action));
        }
    }

    filterAutocompleteOptions(search: string): Observable<SelectableOption[]> {
        if (!this.options$) return of([]);

        if (!search) {
            return this.options$;
        }

        return this.options$.pipe(
            map((options) =>
                options.filter((v) => this.getDisplayText(v).toLowerCase().includes(search.toLowerCase())),
            ),
        );
    }

    onAutocompleteSelected(event: MatAutocompleteSelectedEvent): void {
        if (!this.options$) return;

        const selectedValue = event.option.value;

        // Update the real control when an option is selected
        this.control.setValue(selectedValue);
        this.control.markAsDirty();

        // Process any change events
        const subscription = this.options$
            .pipe(
                map((options) => {
                    const selectedOption = options.find((option) => this.getValue(option) === selectedValue);
                    if (selectedOption && this.field.events?.onChange) {
                        this.field.events.onChange(selectedOption).forEach((action) => this.store.dispatch(action));
                    }
                }),
                take(1),
            )
            .subscribe();

        this.subscriptions.add(subscription);
    }

    // Clear the autocomplete
    clearAutocomplete(event: Event): void {
        if (this.control.disabled) {
            return;
        }

        event.stopPropagation();
        this.store.dispatch(clearAutocomplete({ fieldId: this.fieldId }));
        this.displayControl.setValue('');
    }
}
